import { SystemStyleObject } from "styled-system/types";
import { Button, Icon, Spinner, Text } from "@ttc3k/trekker";
import {
  ForwardRefExoticComponent,
  PropsWithChildren,
  RefAttributes,
  SVGProps,
} from "react";
import { Box, Divider, Flex, styled } from "styled-system/jsx";

export interface IAppViewItem {
  Icon: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  key: string;
  text: string;
  count: number;
  isSelected: boolean;
}

export interface IAppViewSection {
  items: IAppViewItem[];
}

type AppViewItemProps = {
  item: IAppViewItem;
  onClick: (item: IAppViewItem) => void;
};

/**
 * The AppViewItem is a <li> element with an icon, text, and count
 * @param param0 AppViewItemProps
 * @returns
 */
const AppViewItem = ({ item, onClick }: AppViewItemProps) => {
  return (
    <li>
      <Button
        visual="unstyled"
        display="flex"
        flexDir="row"
        justifyContent="space-between"
        alignItems="center"
        gap="250"
        cursor="pointer"
        width="full"
        color={item.isSelected ? "text.dark" : "text.mid"}
        onClick={() => onClick(item)}
      >
        <Flex justifyContent="space-between" alignItems="center" gap="8px">
          <Icon Element={item.Icon} />
          <Text visual={"bodyMedium"}>{item.text}</Text>
        </Flex>
        <Flex
          alignItems={"center"}
          height={"24px"}
          backgroundColor={item.isSelected ? "bg.darkest" : "bg.lightest"}
          rounded={"50"}
        >
          {item.count !== -1 ? (
            <Text
              color={item.isSelected ? "text.inverse" : "text.dark"}
              textAlign="center"
              visual="smallTag"
              px={100}
            >
              {item.count}
            </Text>
          ) : (
            <Spinner w="10px" h="10px" mx={100} />
          )}
        </Flex>
      </Button>
    </li>
  );
};

/**
 * The AppViewSection is a list and starts with a divider line
 * - Its children are AppViewItems (<li> elements)
 * @param param0
 * @returns
 */
const AppViewSection = ({ children }: PropsWithChildren) => {
  return (
    <>
      <Divider color="bg.lighter" my="250" />
      <styled.ul
        display="flex"
        flexDir="column"
        gap="15px"
        listStyle="none"
        margin={0}
      >
        {children}
      </styled.ul>
    </>
  );
};

type AppViewSurfaceProps = {
  title: string;
} & SystemStyleObject &
  PropsWithChildren;

/**
 * The AppViewSurface is the base layer for the AppView
 * - It holds the title and lists
 * @param param0 AppViewSurfaceProps
 * @returns
 */
const AppViewSurface = ({ title, children, ...props }: AppViewSurfaceProps) => {
  return (
    <Box {...props}>
      <Text color="text.light" visual="smallLabel">
        {title}
      </Text>
      {children}
    </Box>
  );
};

type AppViewProps = {
  title?: string;
  sections?: IAppViewSection[];
  onSelectionChange?: (item: IAppViewItem) => void;
} & SystemStyleObject &
  PropsWithChildren;

/**
 * The AppView consists of
 * - The Title below the main left sidebar content (e.g. VIEWS)
 * - The list of view section groups
 * @param param0 AppViewProps
 * @returns
 */
export const AppView = ({
  title,
  sections,
  onSelectionChange,
  ...props
}: AppViewProps) => {
  if (!title || !sections) {
    return null;
  }

  return (
    <AppViewSurface title={title} {...props}>
      {sections.map((section, i) => (
        <AppViewSection key={`AppViewSection_${i}`}>
          {section.items.map((item) => (
            <AppViewItem
              key={item.key}
              item={item}
              onClick={(item) => onSelectionChange?.(item)}
            />
          ))}
        </AppViewSection>
      ))}
    </AppViewSurface>
  );
};
